import { useCallback, useState } from 'react';
import API_CONFIG from '../../../config/api';

const useProductKnowledgeData = ({ selectedProject, setSnackbar }) => {
  const [staged, setStaged] = useState([]);
  const [approved, setApproved] = useState([]);
  const [deleted, setDeleted] = useState([]);
  const [generatedItems, setGeneratedItems] = useState(new Set());

  const projectId = selectedProject?.project_id;

  const loadStaged = useCallback(async () => {
    try {
      if (!projectId) return;
      const res = await API_CONFIG.request(`/pkb/${projectId}/staged`, { method: 'GET' });
      const json = await res.json();
      console.log('📦 loadStaged response:', json);
      if (json?.success) {
        console.log('✅ Setting staged items:', json.items?.length || 0, 'items');
        setStaged(json.items || []);
      } else {
        console.error('❌ loadStaged failed:', json);
      }
    } catch (error) {
      console.error('❌ loadStaged error:', error);
    }
  }, [projectId]);

  const loadDeleted = useCallback(async () => {
    try {
      if (!projectId) return;
      const res = await API_CONFIG.request(`/pkb/${projectId}/deleted`, { method: 'GET' });
      const json = await res.json();
      if (json?.success) {
        setDeleted(json.items || []);
      }
    } catch (error) {
      // Ignore deleted load failures for now.
    }
  }, [projectId]);

  const loadApproved = useCallback(async () => {
    try {
      if (!projectId) return;
      const res = await API_CONFIG.request(`/pkb/${projectId}/accepted`, { method: 'GET' });
      const json = await res.json();
      if (json?.success) {
        setApproved(json.items || []);
      }
    } catch (error) {
      // Ignore approved load failures for now.
    }
  }, [projectId]);

  const loadGeneratedState = useCallback(async () => {
    try {
      if (!projectId) return new Set();
      const res = await API_CONFIG.request(`/pkb/${projectId}/staged`, { method: 'GET' });
      const json = await res.json();
      if (!json?.success) return new Set();

      const generatedKnowledgeIds = new Set();
      json.items?.forEach((item) => {
        if (item.times_generated && item.times_generated > 0) {
          generatedKnowledgeIds.add(item.knowledge_id);
        }
      });
      setGeneratedItems(generatedKnowledgeIds);
      return generatedKnowledgeIds;
    } catch (error) {
      return new Set();
    }
  }, [projectId]);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadStaged(), loadApproved(), loadDeleted()]);
  }, [loadApproved, loadDeleted, loadStaged]);

  const refreshPrimaryLists = useCallback(async () => {
    await Promise.all([loadStaged(), loadApproved()]);
  }, [loadApproved, loadStaged]);

  const refreshCurrentTab = useCallback((tab) => {
    if (tab === 'staged') return loadStaged();
    if (tab === 'approved') return loadApproved();
    if (tab === 'deleted') return loadDeleted();
    return Promise.resolve();
  }, [loadApproved, loadDeleted, loadStaged]);

  const handleUploadFile = useCallback(async ({ title, file }) => {
    if (!projectId) {
      setSnackbar({ open: true, message: 'Please select a project.', severity: 'error' });
      return;
    }

    try {
      const form = new FormData();
      form.append('title', title);
      form.append('file', file);
      const res = await API_CONFIG.request(`/pkb/${projectId}/ingest/prd-file`, {
        method: 'POST',
        body: form,
      });
      const json = await res.json();
      if (res.ok && json?.success) {
        console.log('🎉 Upload successful, reloading staged items...');
        setSnackbar({
          open: true,
          message: json?.message || `Uploaded ${title} to PKB (staged).`,
          severity: 'success',
        });
        await loadStaged();
        console.log('✅ Staged items reloaded after upload');
      } else {
        throw new Error(json?.detail || `Upload failed (HTTP ${res.status})`);
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Upload failed', severity: 'error' });
    }
  }, [loadStaged, projectId, setSnackbar]);

  const handlePasteText = useCallback(async ({ title, text }) => {
    if (!projectId) {
      setSnackbar({ open: true, message: 'Please select a project.', severity: 'error' });
      return;
    }

    try {
      const res = await API_CONFIG.request(`/pkb/${projectId}/ingest/prd`, {
        method: 'POST',
        body: JSON.stringify({ title, content: text, source_id: 'manual_upload', extraction: 'hybrid' }),
      });
      const json = await res.json();
      if (json?.success) {
        console.log('🎉 Text paste successful, reloading staged items...');
        setSnackbar({ open: true, message: `Saved ${title} to PKB (staged).`, severity: 'success' });
        await loadStaged();
        console.log('✅ Staged items reloaded after text paste');
      } else {
        throw new Error(json?.detail || 'Upload failed');
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Upload failed', severity: 'error' });
    }
  }, [loadStaged, projectId, setSnackbar]);

  const handleJiraImport = useCallback(async ({ title, input }) => {
    if (!projectId) {
      setSnackbar({ open: true, message: 'Please select a project.', severity: 'error' });
      return;
    }

    try {
      const res = await API_CONFIG.request('/jira/import', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          input,
          title,
        }),
      });
      const json = await res.json();
      if (json?.success) {
        console.log('🎉 JIRA import successful, reloading staged items...');
        setSnackbar({
          open: true,
          message: `JIRA import started: ${json.message}`,
          severity: 'success',
        });
        await loadStaged();
        console.log('✅ Staged items reloaded after JIRA import');
      } else {
        throw new Error(json?.detail || 'JIRA import failed');
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'JIRA import failed', severity: 'error' });
    }
  }, [loadStaged, projectId, setSnackbar]);

  const handleMoveToStaged = useCallback(async (ids, onSuccess) => {
    try {
      const res = await API_CONFIG.request(`/pkb/${projectId}/move-to-staged`, {
        method: 'POST',
        body: JSON.stringify({ knowledge_ids: ids }),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) {
        throw new Error(json?.detail || 'Move to Staged failed');
      }
      const count = json.moved_count || 0;
      setSnackbar({
        open: true,
        message: `Moved ${count} item(s) to Staged.`,
        severity: 'success',
      });
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
      await refreshAll();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Move to Staged failed',
        severity: 'error',
      });
    }
  }, [projectId, refreshAll, setSnackbar]);

  const handleBulkAction = useCallback(async (action, ids, onSuccess) => {
    try {
      const res = await API_CONFIG.request(`/pkb/${projectId}/${action}`, {
        method: 'POST',
        body: JSON.stringify({ knowledge_ids: ids }),
      });
      const json = await res.json();
      if (json?.success) {
        const count = json[action === 'promote' ? 'promoted' : action === 'delete' ? 'deleted' : 'restored'] || 0;
        setSnackbar({
          open: true,
          message: `${action.charAt(0).toUpperCase() + action.slice(1)}d ${count} entries.`,
          severity: 'success',
        });
        if (typeof onSuccess === 'function') {
          onSuccess();
        }
        await refreshAll();
      } else {
        throw new Error(json?.detail || `${action} failed`);
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.message || `${action} failed`, severity: 'error' });
    }
  }, [projectId, refreshAll, setSnackbar]);

  return {
    staged,
    approved,
    deleted,
    generatedItems,
    setGeneratedItems,
    loadStaged,
    loadApproved,
    loadDeleted,
    loadGeneratedState,
    refreshAll,
    refreshPrimaryLists,
    refreshCurrentTab,
    handleUploadFile,
    handlePasteText,
    handleJiraImport,
    handleBulkAction,
    handleMoveToStaged,
  };
};

export default useProductKnowledgeData;
